import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  HiHome,
  HiClock,
  HiShieldCheck,
  HiHeart,
  HiUserGroup,
  HiTrash,
  HiKey,
  HiBuildingOffice,
  HiUsers,
  HiAcademicCap,
  HiChatBubbleLeftRight,
  HiUserCircle,
  HiClipboardDocumentList,
  HiRocketLaunch,
  HiDocumentText,
  HiCheckCircle,
  HiBookOpen,
  HiChartBar,
  HiLockClosed,
  HiDocumentDuplicate,
  HiFolder,
  HiExclamationTriangle,
  HiCog6Tooth,
  HiGlobeAlt,
  HiChevronDoubleLeft,
  HiChevronDoubleRight,
  HiChevronDown,
} from 'react-icons/hi2';
import { onAuthStateChanged } from 'firebase/auth';
import { auth, firestore } from '../../../database/firebase';
import { doc, getDoc } from 'firebase/firestore';
import { getNavGroups } from '../../../dev-dashboard/config/navConfig';

const iconMap = {
  HiHome,
  HiClock,
  HiShieldCheck,
  HiHeart,
  HiUserGroup,
  HiTrash,
  HiKey,
  HiBuildingOffice,
  HiUsers,
  HiAcademicCap,
  HiChatBubbleLeftRight,
  HiUserCircle,
  HiClipboardDocumentList,
  HiRocketLaunch,
  HiDocumentText,
  HiCheckCircle,
  HiBookOpen,
  HiChartBar,
  HiLockClosed,
  HiDocumentDuplicate,
  HiFolder,
  HiExclamationTriangle,
  HiCog6Tooth,
  HiGlobeAlt,
};

const DevSidebar = ({ collapsed, setCollapsed, selected, setSelected }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [navGroups, setNavGroups] = useState([]);
  const [expandedGroups, setExpandedGroups] = useState(new Set(['A', 'B', 'C']));

  useEffect(() => {
    setNavGroups(getNavGroups());
  }, []);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async currentUser => {
      setLoading(true);
      if (currentUser) {
        setUser(currentUser);
        try {
          const userDocRef = doc(firestore, 'developer', currentUser.uid);
          const userDocSnap = await getDoc(userDocRef);
          setUserProfile(userDocSnap.exists() ? userDocSnap.data() : null);
        } catch {
          setUserProfile(null);
        }
      } else {
        setUser(null);
        setUserProfile(null);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const path = location.pathname;
    const map = [
      ['/overview/activity', 'activity'],
      ['/overview', 'overview'],
      ['/collections', 'collections'],
      ['/security/health', 'system-health'],
      ['/security', 'security'],
      ['/identity', 'identity'],
      ['/clients', 'clients'],
      ['/programs', 'programs'],
      ['/product-dev', 'product-dev'],
      ['/knowledge', 'knowledge'],
      ['/messaging', 'messaging'],
      ['/templates', 'templates'],
      ['/documents', 'documents'],
      ['/data-quality', 'data-quality'],
      ['/tools', 'tools'],
      ['/http', 'http'],
    ];
    for (const [p, id] of map) {
      if (path.includes(p)) {
        setSelected(id);
        break;
      }
    }
  }, [location.pathname, setSelected]);

  const displayData = {
    firstName: userProfile?.firstname || user?.displayName?.split(' ')[0] || 'Developer',
    lastName: userProfile?.lastname || user?.displayName?.split(' ')[1] || '',
    email: user?.email || '',
  };
  const initials =
    `${displayData.firstName.charAt(0)}${displayData.lastName.charAt(0)}`.toUpperCase();

  const toggleGroup = g => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(g)) next.delete(g);
      else next.add(g);
      return next;
    });
  };

  const handleNavClick = item => {
    setSelected(item.id);
    navigate(item.path);
    if (window.innerWidth < 1024) setCollapsed(true);
  };

  return (
    <motion.aside
      animate={{ width: collapsed ? 72 : 260 }}
      transition={{ duration: 0.25, ease: 'easeInOut' }}
      className="fixed z-50 flex h-full flex-col border-r border-slate-700/50 bg-slate-900 shadow-2xl lg:relative"
    >
      {/* Logo / Collapse */}
      <div className="flex h-14 items-center justify-between border-b border-slate-700/50 px-3">
        {!collapsed && (
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              <HiCog6Tooth className="h-4 w-4 text-white" />
            </div>
            <span className="text-sm font-bold text-white">411 Dev</span>
          </div>
        )}
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={() => setCollapsed(!collapsed)}
          className="rounded-lg p-2 text-slate-400 transition-colors hover:bg-slate-800 hover:text-white"
        >
          {collapsed ? (
            <HiChevronDoubleRight className="h-4 w-4" />
          ) : (
            <HiChevronDoubleLeft className="h-4 w-4" />
          )}
        </motion.button>
      </div>
      <AnimatePresence>
        {!collapsed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="border-b border-slate-700/50 px-3 py-4"
          >
            {user ? (
              <div className="flex items-center gap-3">
                <div className="relative">
                  <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-700 text-sm font-semibold text-slate-200">
                    {initials}
                  </div>
                  <div className="absolute -bottom-0.5 -right-0.5 h-2.5 w-2.5 rounded-full border-2 border-slate-900 bg-emerald-500" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-white">
                    {displayData.firstName} {displayData.lastName}
                  </div>
                  <div className="truncate text-xs text-slate-400">{displayData.email}</div>
                </div>
              </div>
            ) : (
              !loading && (
                <div className="flex items-center gap-2 text-slate-500">
                  <HiUserCircle className="h-8 w-8" />
                  <span className="text-xs">Not logged in</span>
                </div>
              )
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Nav - Expandable sections */}
      <nav className="flex-1 overflow-y-auto py-3">
        {navGroups.map(group => {
          const isExpanded = collapsed ? true : expandedGroups.has(group.id);

          return (
            <div key={group.id} className="mb-1">
              {!collapsed && (
                <button
                  onClick={() => toggleGroup(group.id)}
                  className="flex w-full items-center justify-between px-3 py-2.5 text-left text-xs font-semibold uppercase tracking-wider text-slate-500 transition-colors hover:bg-slate-800/50 hover:text-slate-300"
                >
                  <span>{group.label}</span>
                  <motion.span
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <HiChevronDown className="h-3.5 w-3.5" />
                  </motion.span>
                </button>
              )}
              <AnimatePresence>
                {(isExpanded || collapsed) && (
                  <motion.div
                    initial={collapsed ? false : { height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={collapsed ? false : { height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    {group.items.map(item => {
                      const Icon = iconMap[item.icon] || HiHome;
                      const isSelected = selected === item.id;

                      return (
                        <button
                          key={item.id}
                          onClick={() => handleNavClick(item)}
                          title={collapsed ? item.label : undefined}
                          className={`flex w-full items-center gap-3 px-3 py-2.5 text-left transition-all ${
                            collapsed ? 'justify-center' : ''
                          } ${
                            isSelected
                              ? 'border-l-2 border-blue-500 bg-blue-500/10 text-white'
                              : 'border-l-2 border-transparent text-slate-400 hover:bg-slate-800/50 hover:text-slate-200'
                          }`}
                        >
                          <Icon
                            className={`h-4 w-4 shrink-0 ${isSelected ? 'text-blue-400' : 'text-slate-500'}`}
                          />
                          {!collapsed && (
                            <span
                              className={`text-sm ${isSelected ? 'font-semibold' : 'font-medium'}`}
                            >
                              {item.label}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          );
        })}
      </nav>
      {!collapsed && (
        <div className="border-t border-slate-700/50 px-3 py-3">
          <div className="text-center text-[10px] text-slate-500">© 2026 411 Group</div>
        </div>
      )}
    </motion.aside>
  );
};

export default DevSidebar;
